import { useState } from 'react';
import {
  ChevronLeft,
  Keyboard,
  Smartphone,
  FileSpreadsheet,
  Boxes,
} from 'lucide-react';
import { admin, palette, radii, shadow, transition, typography } from '../../../styles/tokens';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import CompetitorPath from './competitor/CompetitorPath';
import ConversationalPath from './conversational/ConversationalPath';
import SpreadsheetPath from './spreadsheet/SpreadsheetPath';
import type {
  ImportClientsCardConfig,
  ImportClientsCardId,
  ImportSkipReason,
} from './types';

interface ImportClientsItemProps {
  onBack: () => void;
  onSaved: () => void;
  skipItem: (key: string, reason?: string) => Promise<void>;
}

// FROZEN order — do not reorder, do not make industry-aware, do not rank.
const CARDS: ImportClientsCardConfig[] = [
  {
    id: 'conversational',
    title: 'Type them in',
    subtitle: 'Add clients one at a time',
    iconName: 'Keyboard',
  },
  {
    id: 'phone_contacts',
    title: 'From your phone contacts',
    subtitle: 'Import from iPhone contacts',
    iconName: 'Smartphone',
    disabled: true,
    disabledReason: 'Open CrewBit on your phone to use this',
  },
  {
    id: 'spreadsheet',
    title: 'From a spreadsheet',
    subtitle: 'Upload a CSV or Excel file',
    iconName: 'FileSpreadsheet',
  },
  {
    id: 'competitor',
    title: 'From another tool',
    subtitle: 'Square, Vagaro, Jobber, and more',
    iconName: 'Boxes',
  },
];

function iconFor(name: string, disabled: boolean) {
  const colour = disabled ? palette.g400 : palette.accent;
  const props = { size: 28, color: colour, strokeWidth: 1.75 };
  switch (name) {
    case 'Keyboard':
      return <Keyboard {...props} />;
    case 'Smartphone':
      return <Smartphone {...props} />;
    case 'FileSpreadsheet':
      return <FileSpreadsheet {...props} />;
    case 'Boxes':
      return <Boxes {...props} />;
    default:
      return null;
  }
}

export default function ImportClientsItem({
  onBack,
  onSaved,
  skipItem,
}: ImportClientsItemProps) {
  // `null` = entry grid; a path id routes to one of the four path shells.
  const [activePath, setActivePath] = useState<ImportClientsCardId | null>(null);
  const [skipping, setSkipping] = useState<ImportSkipReason | null>(null);
  // C-020 (Q7): bubbled up from ConversationalPath so the skip-confirm dialog
  // can render an accurate discard count even after the user navigates back
  // to the entry grid (ConversationalPath unmounts but its last reported
  // unsaved count survives here).
  const [unsavedCount, setUnsavedCount] = useState(0);
  const [confirmDeferredSkip, setConfirmDeferredSkip] = useState(false);

  const handleCardClick = (card: ImportClientsCardConfig) => {
    if (card.disabled) return;
    setActivePath(card.id);
  };

  const performSkip = async (reason: ImportSkipReason) => {
    if (skipping) return;
    setSkipping(reason);
    await skipItem('import_clients', reason);
    // skipItem triggers its own refresh, but onSaved unmounts this screen and
    // navigates back to the Setup Guide home where the item will render ✓.
    onSaved();
  };

  const handleSkip = async (reason: ImportSkipReason) => {
    if (reason === 'deferred' && unsavedCount > 0) {
      setConfirmDeferredSkip(true);
      return;
    }
    await performSkip(reason);
  };

  if (activePath === 'conversational') {
    return (
      <ConversationalPath
        onBackToGrid={() => setActivePath(null)}
        onSaved={onSaved}
        // Handoff to Path 3: user signalled "switch to spreadsheet" from a
        // tiered banner. We just swap active view — the already-inserted
        // rows live in the DB and Path 3's preview step will surface them
        // via findDuplicates, with merge writeback handling any fill-in.
        onSwitchToSpreadsheet={() => setActivePath('spreadsheet')}
        onUnsavedCountChange={setUnsavedCount}
      />
    );
  }

  if (activePath === 'spreadsheet') {
    return (
      <SpreadsheetPath
        onBackToGrid={() => setActivePath(null)}
        onSaved={onSaved}
      />
    );
  }

  if (activePath === 'competitor') {
    return (
      <CompetitorPath
        onBackToGrid={() => setActivePath(null)}
        onSaved={onSaved}
      />
    );
  }

  if (activePath) {
    return (
      <ComingSoonPlaceholder
        path={activePath}
        onBack={() => setActivePath(null)}
      />
    );
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '8px 4px 0' }}>
      <button
        type="button"
        onClick={onBack}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '6px 8px 6px 4px',
          marginBottom: 16,
          background: 'transparent',
          border: 'none',
          color: admin.caption,
          fontSize: typography.size.caption,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        <ChevronLeft size={16} />
        <span>Setup guide</span>
        <span style={{ color: palette.g400, margin: '0 4px' }}>›</span>
        <span style={{ color: admin.body }}>Import your clients</span>
      </button>

      <h1
        style={{
          margin: 0,
          fontSize: typography.size.title,
          fontWeight: typography.weight.bold,
          color: admin.header,
          letterSpacing: '-0.01em',
          marginBottom: 8,
        }}
      >
        Import your clients
      </h1>
      <p
        style={{
          margin: 0,
          marginBottom: 24,
          fontSize: typography.size.body,
          color: admin.body,
          lineHeight: typography.lineHeight.relaxed,
        }}
      >
        Pick the way that fits how your client list lives today.
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 16,
          marginBottom: 32,
        }}
      >
        {CARDS.map((card) => {
          const disabled = !!card.disabled;
          return (
            <button
              key={card.id}
              type="button"
              onClick={() => handleCardClick(card)}
              disabled={disabled}
              style={{
                textAlign: 'left',
                padding: 20,
                background: palette.white,
                border: `1px solid ${palette.g150}`,
                borderRadius: radii.md,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                boxShadow: shadow.xs,
                transition: `box-shadow ${transition.normal}, border-color ${transition.normal}`,
                fontFamily: 'inherit',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                minHeight: 148,
              }}
              onMouseEnter={(e) => {
                if (disabled) return;
                e.currentTarget.style.boxShadow = shadow.cardHover;
                e.currentTarget.style.borderColor = admin.borderHover;
              }}
              onMouseLeave={(e) => {
                if (disabled) return;
                e.currentTarget.style.boxShadow = shadow.xs;
                e.currentTarget.style.borderColor = palette.g150;
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: radii.sm,
                  background: disabled ? palette.g100 : palette.accentWash,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {iconFor(card.iconName, disabled)}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div
                  style={{
                    fontSize: typography.size.heading,
                    fontWeight: typography.weight.semibold,
                    color: admin.header,
                    letterSpacing: '-0.005em',
                  }}
                >
                  {card.title}
                </div>
                <div
                  style={{
                    fontSize: typography.size.caption,
                    color: admin.caption,
                    lineHeight: typography.lineHeight.normal,
                  }}
                >
                  {disabled && card.disabledReason
                    ? card.disabledReason
                    : card.subtitle}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
          paddingBottom: 32,
        }}
      >
        <SkipLink
          label="I don't have a client list to import"
          onClick={() => handleSkip('not_applicable')}
          pending={skipping === 'not_applicable'}
          disabled={!!skipping}
        />
        <SkipLink
          label="I'll do this later"
          onClick={() => handleSkip('deferred')}
          pending={skipping === 'deferred'}
          disabled={!!skipping}
        />
      </div>

      <ConfirmDialog
        isOpen={confirmDeferredSkip}
        title="Skip for now?"
        message={`You have ${unsavedCount} ${unsavedCount === 1 ? 'client entry' : 'client entries'} not yet saved. They will be discarded. Already-saved entries stay in your records — you can add more later from Settings → Clients.`}
        confirmLabel="Skip and discard"
        confirmVariant="danger"
        onClose={() => setConfirmDeferredSkip(false)}
        onConfirm={() => {
          setConfirmDeferredSkip(false);
          void performSkip('deferred');
        }}
      />
    </div>
  );
}

interface SkipLinkProps {
  label: string;
  onClick: () => void;
  pending: boolean;
  disabled: boolean;
}

function SkipLink({ label, onClick, pending, disabled }: SkipLinkProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        background: 'transparent',
        border: 'none',
        padding: '4px 8px',
        color: admin.caption,
        fontSize: typography.size.caption,
        fontFamily: 'inherit',
        cursor: disabled ? 'default' : 'pointer',
        textDecoration: 'none',
        opacity: disabled && !pending ? 0.5 : 1,
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.textDecoration = 'underline';
        e.currentTarget.style.color = admin.body;
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        e.currentTarget.style.textDecoration = 'none';
        e.currentTarget.style.color = admin.caption;
      }}
    >
      {pending ? 'Saving…' : label}
    </button>
  );
}

interface ComingSoonPlaceholderProps {
  path: ImportClientsCardId;
  onBack: () => void;
}

function ComingSoonPlaceholder({ path, onBack }: ComingSoonPlaceholderProps) {
  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '8px 4px 0' }}>
      <button
        type="button"
        onClick={onBack}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '6px 8px 6px 4px',
          marginBottom: 16,
          background: 'transparent',
          border: 'none',
          color: admin.caption,
          fontSize: typography.size.caption,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        <ChevronLeft size={16} />
        <span>Import your clients</span>
      </button>

      <div
        style={{
          background: palette.g50,
          border: `1px solid ${palette.g150}`,
          borderRadius: radii.md,
          padding: 48,
          textAlign: 'center',
          fontSize: typography.size.body,
          color: admin.body,
          lineHeight: typography.lineHeight.relaxed,
        }}
      >
        <div
          style={{
            fontSize: typography.size.heading,
            fontWeight: typography.weight.semibold,
            color: admin.header,
            marginBottom: 8,
          }}
        >
          Coming soon
        </div>
        <div style={{ color: admin.caption, fontSize: typography.size.caption }}>
          This import path ships in a later build.
          <span style={{ display: 'block', marginTop: 4, color: palette.g400 }}>
            ({path})
          </span>
        </div>
      </div>
    </div>
  );
}
